import type {ReactElement} from 'react';
import React, {useId, useRef} from 'react';
import cx from 'classnames';
import {v4 as uuid} from 'uuid';

import {FontVariant} from '../../../../design-tokens/generated/constants';
import {TestId} from '../../../utils/testIds';
import {SvgImage} from '../SvgImage/SvgImage';
import {Text} from '../Text/Text';
import {ThemeProvider} from '../../../themes/ThemeContext';
import {Box} from '../Box/Box';
import {Button} from '../Button/Button';
import type {ColorScheme} from '../../../types/variants';
import {ModalCloseButton} from '../../modules/CloseButton/ModalCloseButton.view';

import ArrowSvg from './arrow.svg';
import type {ControlledTooltip} from './useTooltip';
import {useTooltip} from './useTooltip';

import styles from './Tooltip.module.scss';
import {useAriaTooltip} from '../../../hooks/useAriaTooltip';
import {useNotificationTooltipInteractions} from '../../../hooks/useNotificationTooltipInteractions';

export type NotificationTooltipPosition =
    | 'topRight'
    | 'topCenter'
    | 'topLeft'
    | 'topStart'
    | 'topEnd'
    | 'bottomRight'
    | 'bottomCenter'
    | 'bottomLeft'
    | 'bottomStart'
    | 'bottomEnd'
    | 'left'
    | 'right';

export const NotificationTooltip: React.FC<
    React.ComponentProps<'div'> &
        ControlledTooltip & {
            position?: NotificationTooltipPosition;
            header?: React.ReactNode;
            modifier?: 'fullWidth';
            text: string | ReactElement;
            ctaText?: string | ReactElement;
            ctaFn?: () => void;
            isArrowShown?: boolean;
            testIdInner?: string;
            testIdCloseButton?: string;
            colorScheme?: ColorScheme;
            mainWrapperClassName?: string;
            ariaLabel?: string;
            closeButtonAriaLabel?: string;
            stopPointerDownPropagation?: boolean;
            shouldCloseOnOutsideClick?: boolean;
            zIndex?: number | string;
        }
> = ({
    ariaLabel,
    closeButtonAriaLabel = 'Close tooltip',
    shouldCloseOnOutsideClick = false,
    colorScheme = 'light',
    position = 'topRight',
    header,
    modifier,
    text,
    ctaText,
    ctaFn = () => {
        /*noop*/
    },
    controlledIsVisible,
    controlledOnChange,
    children,
    className,
    mainWrapperClassName,
    isArrowShown = true,
    testIdInner = TestId.NotificationTooltipInner,
    testIdCloseButton = TestId.NotificationTooltipClose,
    stopPointerDownPropagation = false,
    // Default to the `components-z-index-tooltip` token (react-aria's top layer)
    // so apps can tune tooltip stacking via the token.
    zIndex = 'var(--components-z-index-tooltip)',
}) => {
    const {isVisible, setVisible} = useTooltip({
        controlledIsVisible,
        controlledOnChange,
    });
    const portalIdRef = useRef(uuid());
    const headerId = useId();
    const descriptionId = useId();
    const {
        close,
        handleGuardFocus,
        handleOpenChange,
        handleTooltipEndGuardFocus,
        handleTooltipKeyDown,
        handleTooltipStartGuardFocus,
        outsideClickRef,
        overlayRef,
        tabOrderGuardRef,
        targetRef,
        tooltipEndGuardRef,
        tooltipStartGuardRef,
    } = useNotificationTooltipInteractions({
        setVisible,
        shouldCloseOnOutsideClick,
    });

    const {arrowProps, placement, renderTooltip} = useAriaTooltip({
        ariaDescribedBy:
            (ariaLabel || header) && text ? descriptionId : undefined,
        ariaLabel,
        ariaLabelledBy: ariaLabel
            ? undefined
            : header
              ? headerId
              : descriptionId,
        id: portalIdRef.current,
        isOpen: isVisible,
        onOpenChange: handleOpenChange,
        overlayRef,
        position,
        role: 'dialog',
        targetRef,
        zIndex,
    });

    const tooltipContent = (
        <ThemeProvider colorScheme={colorScheme}>
            <Box
                data-portal-id={portalIdRef.current}
                ref={outsideClickRef}
                className={cx(
                    styles.tooltip,
                    styles.tooltipNotification,
                    modifier && styles[modifier],
                    className,
                )}
                data-placement={placement}
                data-testid={testIdInner}
                onKeyDownCapture={handleTooltipKeyDown}
                onPointerDownCapture={
                    stopPointerDownPropagation
                        ? e => e.stopPropagation()
                        : undefined
                }
            >
                {/* biome-ignore lint/a11y/noStaticElementInteractions: This guard prevents native tab order from entering the portal. */}
                <span
                    ref={tooltipStartGuardRef}
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: The guard immediately routes focus past the portalled dialog.
                    tabIndex={0}
                    onFocus={handleTooltipStartGuardFocus}
                    className={styles.tabOrderGuard}
                />
                {header && (
                    <div className={styles.headerWrapper}>
                        {typeof header === 'string' ? (
                            <Text
                                id={headerId}
                                fontVariant={FontVariant.BodyBold}
                                data-testid={TestId.NotificationTooltipHeader}
                            >
                                {header}
                            </Text>
                        ) : (
                            <div
                                id={headerId}
                                data-testid={TestId.NotificationTooltipHeader}
                            >
                                {header}
                            </div>
                        )}
                        <ModalCloseButton
                            className={styles.closeButton}
                            data-testid={testIdCloseButton}
                            onClose={close}
                            aria-label={closeButtonAriaLabel}
                        />
                    </div>
                )}
                <Text
                    id={descriptionId}
                    fontVariant={FontVariant.Body}
                    data-testid={TestId.NotificationTooltipText}
                >
                    {text}
                </Text>
                {ctaText &&
                    (typeof ctaText === 'string' ? (
                        <Button
                            variant="text"
                            size="none"
                            fontVariant={FontVariant.BodyLink}
                            useTextComponent
                            underlineText
                            onClick={ctaFn}
                            className={styles.cta}
                            data-testid={TestId.NotificationTooltipCta}
                        >
                            {ctaText}
                        </Button>
                    ) : (
                        ctaText
                    ))}

                {isArrowShown && (
                    <div className={styles.tooltipArrow} {...arrowProps}>
                        <SvgImage
                            source={ArrowSvg}
                            className={styles.tooltipArrowIcon}
                        />
                    </div>
                )}
                {/* biome-ignore lint/a11y/noStaticElementInteractions: This guard prevents native reverse tab order from entering the portal. */}
                <span
                    ref={tooltipEndGuardRef}
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: The guard immediately routes focus past the portalled dialog.
                    tabIndex={0}
                    onFocus={handleTooltipEndGuardFocus}
                    className={styles.tabOrderGuard}
                />
            </Box>
        </ThemeProvider>
    );

    return (
        <div
            data-portal-origin-id={portalIdRef.current}
            {...(isVisible
                ? {
                      role: 'group',
                      'aria-label': ariaLabel,
                      'aria-labelledby': ariaLabel
                          ? undefined
                          : header
                            ? headerId
                            : descriptionId,
                      'aria-describedby':
                          (ariaLabel || header) && text
                              ? descriptionId
                              : undefined,
                      'aria-details': portalIdRef.current,
                      'aria-owns': portalIdRef.current,
                  }
                : {})}
            ref={targetRef}
            className={cx(
                styles.tooltipContainer,
                modifier && styles[modifier],
                mainWrapperClassName,
            )}
        >
            {children}
            {isVisible && (
                /* biome-ignore lint/a11y/noStaticElementInteractions: This focus guard preserves logical keyboard order across the portal. */
                <span
                    ref={tabOrderGuardRef}
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: The guard immediately moves focus into the portalled dialog.
                    tabIndex={0}
                    onFocus={handleGuardFocus}
                    className={styles.tabOrderGuard}
                />
            )}
            {renderTooltip(tooltipContent)}
        </div>
    );
};

export type NotificationTooltipProps = React.ComponentProps<
    typeof NotificationTooltip
>;
